import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Plus, Trash2, CheckCircle2, Clock, AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/i18n-hook';
import {
  DocumentoMaquina,
  EVENTO_DOCUMENTOS_MAQUINA,
  STATUS_DOCUMENTO_MAQUINA,
  StatusDocumentoMaquina,
  TIPOS_DOCUMENTO_MAQUINA,
  adicionarDocumentoMaquina,
  alterarStatusDocumentoMaquina,
  documentoAtrasado,
  listarDocumentosMaquina,
  removerDocumentoMaquina,
  resumirDocumentosMaquina,
} from '@/lib/mock-documentos-maquina';

interface PainelDocumentosProps {
  maquinaId: string;
  nomeMaquina: string;
}

const CLASSE_CAMPO =
  'block w-full px-3 py-2.5 text-sm border border-rg-line dark:border-schmersal-gray-dark rounded-lg ' +
  'focus:outline-none focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue ' +
  'bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white ' +
  'placeholder-schmersal-gray-medium transition-all duration-200';

function classeStatus(status: StatusDocumentoMaquina): string {
  if (status === 'entregue') return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
  if (status === 'em_analise') return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
  return 'bg-schmersal-blue/10 dark:bg-schmersal-blue/25 text-schmersal-blue dark:text-schmersal-blue-light';
}

/** `Date` → `YYYY-MM-DD` para `<input type="date">` (que só aceita esse formato). */
function paraCampoData(data: Date): string {
  return data.toISOString().slice(0, 10);
}

/**
 * Acervo documental de uma máquina.
 *
 * PROTÓTIPO: os documentos saem de `@/lib/mock-documentos-maquina` — gerados a
 * partir do id da máquina e guardados no navegador. Trocar por chamadas ao
 * `apiService` quando o acervo existir no banco não muda esta tela, só a origem
 * dos dados.
 */
export function PainelDocumentos({ maquinaId, nomeMaquina }: PainelDocumentosProps) {
  const { t, formatarData } = useI18n();
  const [documentos, setDocumentos] = useState<DocumentoMaquina[]>([]);
  const [modalAberto, setModalAberto] = useState(false);

  const recarregar = useCallback(() => {
    setDocumentos(listarDocumentosMaquina(maquinaId));
  }, [maquinaId]);

  useEffect(() => {
    recarregar();
    // Outra tela (ou outra aba) pode mexer no mesmo acervo.
    window.addEventListener(EVENTO_DOCUMENTOS_MAQUINA, recarregar);
    return () => window.removeEventListener(EVENTO_DOCUMENTOS_MAQUINA, recarregar);
  }, [recarregar]);

  const resumo = useMemo(() => resumirDocumentosMaquina(maquinaId), [maquinaId, documentos]);

  const handleRemover = (documento: DocumentoMaquina) => {
    removerDocumentoMaquina(maquinaId, documento.id);
    recarregar();
    toast.success(t('documentos.modal.removido'));
  };

  const handleAlternarStatus = (documento: DocumentoMaquina) => {
    const proximo: StatusDocumentoMaquina = documento.status === 'entregue' ? 'pendente' : 'entregue';
    alterarStatusDocumentoMaquina(maquinaId, documento.id, proximo);
    recarregar();
  };

  const cartoes: Array<{ chave: string; valor: number; icone: typeof FileText; cor: string }> = [
    { chave: 'pendentes', valor: resumo.pendentes, icone: Clock, cor: 'text-schmersal-blue dark:text-schmersal-blue-light' },
    { chave: 'emAnalise', valor: resumo.emAnalise, icone: Loader2, cor: 'text-amber-600 dark:text-amber-400' },
    { chave: 'entregues', valor: resumo.entregues, icone: CheckCircle2, cor: 'text-green-600 dark:text-green-400' },
    { chave: 'atrasados', valor: resumo.atrasados, icone: AlertTriangle, cor: 'text-red-600 dark:text-red-400' },
  ];

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
      {/* Cabeçalho */}
      <div className="px-6 py-5 border-b border-rg-line dark:border-schmersal-gray-dark flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-schmersal-blue dark:text-schmersal-blue-light" />
            <h2 className="text-lg font-bold text-schmersal-gray-dark dark:text-white">
              {t('documentos.painel.titulo')}
            </h2>
            <span
              className="rounded-full bg-schmersal-yellow/25 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-schmersal-blue dark:text-schmersal-yellow"
              title={t('documentos.painel.previaExplicacao')}
            >
              {t('documentos.painel.previa')}
            </span>
          </div>
          <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('documentos.painel.subtitulo')} · {t('documentos.painel.contagem', { total: resumo.total })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalAberto(true)}
          className="inline-flex items-center justify-center px-5 py-2.5 border-2 border-transparent rounded-lg
            shadow-md text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          {t('documentos.painel.adicionar')}
        </button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-rg-hairline dark:divide-schmersal-gray-dark border-b border-rg-line dark:border-schmersal-gray-dark">
        {cartoes.map(({ chave, valor, icone: Icone, cor }) => (
          <div key={chave} className="px-6 py-4">
            <div className="flex items-center gap-2">
              <Icone className={`h-4 w-4 ${cor}`} />
              <span className="text-xs font-semibold uppercase tracking-wide text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {t(`documentos.resumo.${chave}`)}
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold text-schmersal-gray-dark dark:text-white">{valor}</p>
          </div>
        ))}
      </div>

      {/* Lista */}
      {documentos.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-schmersal-gray-medium opacity-40 mb-3" />
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('documentos.painel.vazio')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
            <thead className="bg-schmersal-gray-light dark:bg-schmersal-gray-darkest">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white">
                  {t('documentos.coluna.tipo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white hidden md:table-cell">
                  {t('documentos.coluna.codigo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white">
                  {t('comum.rotulo.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white hidden lg:table-cell">
                  {t('documentos.coluna.responsavel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white hidden sm:table-cell">
                  {t('documentos.coluna.prazo')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-schmersal-gray-dark dark:text-white">
                  {t('comum.rotulo.acoes')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
              {documentos.map((documento) => {
                const atrasado = documentoAtrasado(documento);
                return (
                  <tr
                    key={documento.id}
                    className="hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-darkest transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-schmersal-gray-dark dark:text-white">
                        {t(`documentos.tipo.${documento.tipo}`)}
                      </p>
                      {documento.observacao && (
                        <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5 line-clamp-1">
                          {documento.observacao}
                        </p>
                      )}
                      {documento.arquivo && (
                        <p className="text-xs text-schmersal-blue dark:text-schmersal-blue-light mt-0.5 truncate max-w-[220px]">
                          {documento.arquivo}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-sm text-schmersal-gray-dark dark:text-white">{documento.codigo}</span>
                      <span className="ml-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {t('documentos.coluna.revisao')} {documento.revisao}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${classeStatus(documento.status)}`}>
                          {t(`documentos.status.${documento.status}`)}
                        </span>
                        {atrasado && (
                          <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                            {t('documentos.atrasado')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell text-sm text-schmersal-gray-dark dark:text-white">
                      {documento.responsavel || t('documentos.semEmissao')}
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell text-sm text-schmersal-gray-dark dark:text-white">
                      {formatarData(documento.prazo)}
                      {documento.emitidoEm && (
                        <span className="block text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                          {t('documentos.coluna.emissao')}: {formatarData(documento.emitidoEm)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleAlternarStatus(documento)}
                          className="p-2 rounded-lg text-schmersal-gray-medium hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200"
                          title={documento.status === 'entregue'
                            ? t('documentos.acao.marcarPendente')
                            : t('documentos.acao.marcarEntregue')}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemover(documento)}
                          className="p-2 rounded-lg text-schmersal-gray-medium hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                          title={t('documentos.acao.remover')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalAberto && (
        <ModalNovoDocumento
          nomeMaquina={nomeMaquina}
          onFechar={() => setModalAberto(false)}
          onSalvar={(entrada) => {
            adicionarDocumentoMaquina(maquinaId, entrada);
            recarregar();
            setModalAberto(false);
            toast.success(t('documentos.modal.adicionado'));
          }}
        />
      )}
    </div>
  );
}

interface ModalNovoDocumentoProps {
  nomeMaquina: string;
  onFechar: () => void;
  onSalvar: (entrada: {
    tipo: string;
    codigo: string;
    revisao: number;
    status: StatusDocumentoMaquina;
    responsavel: string;
    prazo: string;
    arquivo?: string;
    observacao?: string;
  }) => void;
}

function ModalNovoDocumento({ nomeMaquina, onFechar, onSalvar }: ModalNovoDocumentoProps) {
  const { t } = useI18n();
  const prazoPadrao = useMemo(() => {
    const data = new Date();
    data.setDate(data.getDate() + 15);
    return paraCampoData(data);
  }, []);

  const [tipo, setTipo] = useState<string>(TIPOS_DOCUMENTO_MAQUINA[0]);
  const [codigo, setCodigo] = useState('');
  const [revisao, setRevisao] = useState('0');
  const [status, setStatus] = useState<StatusDocumentoMaquina>('pendente');
  const [responsavel, setResponsavel] = useState('');
  const [prazo, setPrazo] = useState(prazoPadrao);
  const [arquivo, setArquivo] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');

  const handleSalvar = () => {
    if (!codigo.trim()) {
      setErro(t('documentos.modal.erroCodigo'));
      return;
    }
    onSalvar({
      tipo,
      codigo: codigo.trim(),
      revisao: Number(revisao) || 0,
      status,
      responsavel: responsavel.trim(),
      // Meio-dia evita que o fuso jogue a data para o dia anterior.
      prazo: new Date(`${prazo}T12:00:00`).toISOString(),
      arquivo: arquivo || undefined,
      observacao: observacao.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70" onClick={onFechar} />
        <div className="relative bg-white dark:bg-schmersal-gray-darker rounded-xl shadow-2xl border border-rg-line dark:border-schmersal-gray-dark p-6 w-full max-w-2xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-schmersal-gray-dark dark:text-white">
                {t('documentos.modal.titulo')}
              </h3>
              <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                {t('documentos.modal.subtitulo', { maquina: nomeMaquina })}
              </p>
            </div>
            <button
              type="button"
              onClick={onFechar}
              className="p-1.5 rounded-lg text-schmersal-gray-medium hover:text-schmersal-blue hover:bg-schmersal-blue/10 transition-all duration-200"
              title={t('comum.acao.fechar')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.tipo')}
              </label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={CLASSE_CAMPO}>
                {TIPOS_DOCUMENTO_MAQUINA.map((valor) => (
                  <option key={valor} value={valor}>
                    {t(`documentos.tipo.${valor}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.codigo')}
              </label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => { setCodigo(e.target.value); setErro(''); }}
                placeholder={t('documentos.modal.codigoPlaceholder')}
                className={CLASSE_CAMPO}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.revisao')}
              </label>
              <input
                type="number"
                min="0"
                value={revisao}
                onChange={(e) => setRevisao(e.target.value)}
                className={CLASSE_CAMPO}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.responsavel')}
              </label>
              <input
                type="text"
                value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)}
                placeholder={t('documentos.modal.responsavelPlaceholder')}
                className={CLASSE_CAMPO}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.prazo')}
              </label>
              <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} className={CLASSE_CAMPO} />
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.status')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusDocumentoMaquina)}
                className={CLASSE_CAMPO}
              >
                {STATUS_DOCUMENTO_MAQUINA.map((valor) => (
                  <option key={valor} value={valor}>
                    {t(`documentos.status.${valor}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.arquivo')}
              </label>
              <input
                type="file"
                onChange={(e) => setArquivo(e.target.files?.[0]?.name ?? '')}
                className="block w-full text-sm text-schmersal-gray-dark dark:text-white
                  file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold
                  file:bg-schmersal-blue/10 file:text-schmersal-blue dark:file:bg-schmersal-blue/25 dark:file:text-white"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('documentos.modal.observacao')}
              </label>
              <textarea
                rows={3}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder={t('documentos.modal.observacaoPlaceholder')}
                className={CLASSE_CAMPO}
              />
            </div>
          </div>

          {erro && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-3">
              {erro}
            </p>
          )}

          <p className="mt-4 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('documentos.painel.previaExplicacao')}
          </p>

          <div className="mt-4 flex gap-3 justify-end">
            <button
              type="button"
              onClick={onFechar}
              className="px-4 py-2 text-sm font-semibold border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                text-schmersal-gray-dark dark:text-white hover:border-schmersal-blue hover:text-schmersal-blue transition-all duration-200"
            >
              {t('comum.acao.cancelar')}
            </button>
            <button
              type="button"
              onClick={handleSalvar}
              className="px-4 py-2 text-sm font-semibold rounded-lg border-2 border-transparent bg-schmersal-blue text-white
                hover:bg-schmersal-blue-dark transition-all duration-200"
            >
              {t('comum.acao.adicionar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
